import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Plus, Trash2, Pencil } from "lucide-react"
import { api, ApiError, type Tag } from "@/lib/types"
import { GlarchiveSubNav } from "@/components/GlarchiveSubNav"
import { Drawer } from "@lilbillbiscuit/account-ui"

export function AdminTags() {
  const qc = useQueryClient()
  const tags = useQuery<{ tags: Tag[] }>({
    queryKey: ["adminTagsAll"],
    queryFn: () => api<{ tags: Tag[] }>("/api/admin/tags"),
  })

  const [editing, setEditing] = useState<Tag | "new" | null>(null)

  const save = useMutation<unknown, ApiError, { slug?: string; name: string; description: string; isNew: boolean; targetSlug?: string }>({
    mutationFn: async ({ slug, name, description, isNew, targetSlug }) => {
      if (isNew) {
        return api("/api/admin/tags", { method: "POST", json: { slug, name, description } })
      }
      return api(`/api/admin/tags/${encodeURIComponent(targetSlug!)}`, { method: "PATCH", json: { name, description } })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["adminTagsAll"] })
      setEditing(null)
    },
  })

  const del = useMutation<unknown, ApiError, string>({
    mutationFn: (slug) => api(`/api/admin/tags/${encodeURIComponent(slug)}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adminTagsAll"] }),
  })

  return (
    <div className="space-y-6">
      <GlarchiveSubNav />
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-semibold">Tags</h1>
        <button className="btn-primary" onClick={() => setEditing("new")}>
          <Plus size={14} /> New tag
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-th-glass/[0.08]">
        <table className="w-full text-sm">
          <thead className="bg-th-glass/[0.04] text-left text-xs uppercase tracking-wide text-th-fg/50">
            <tr>
              <th className="px-4 py-2 w-40">Slug</th>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Description</th>
              <th className="px-4 py-2 w-24 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tags.data?.tags.map((t) => (
              <tr key={t.id} className="border-t border-th-glass/[0.06] hover:bg-th-glass/[0.04]">
                <td className="px-4 py-2 font-mono text-xs text-th-accent">{t.slug}</td>
                <td className="px-4 py-2">{t.name}</td>
                <td className="px-4 py-2 text-th-fg/60">{t.description || "—"}</td>
                <td className="px-4 py-2 text-right">
                  <button className="btn-ghost" onClick={() => setEditing(t)} aria-label="Edit">
                    <Pencil size={13} />
                  </button>
                  <button
                    className="btn-ghost text-red-400 hover:text-red-300"
                    onClick={() => confirm(`Delete tag "${t.slug}"? This removes it from all users and videos.`) && del.mutate(t.slug)}
                    aria-label="Delete"
                  >
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
            {!tags.isLoading && (tags.data?.tags.length ?? 0) === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-th-fg/40">No tags yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Drawer
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === "new" ? "New tag" : "Edit tag"}
      >
        {editing !== null && (
          <TagForm
            initial={editing === "new" ? null : editing}
            onSubmit={(v) => save.mutate({
              ...v,
              isNew: editing === "new",
              targetSlug: editing === "new" ? undefined : (editing as Tag).slug,
            })}
            error={save.error}
            submitting={save.isPending}
          />
        )}
      </Drawer>
    </div>
  )
}

function TagForm({
  initial, onSubmit, error, submitting,
}: { initial: Tag | null; onSubmit: (v: { slug: string; name: string; description: string }) => void; error: ApiError | null; submitting: boolean }) {
  const [slug, setSlug] = useState(initial?.slug ?? "")
  const [name, setName] = useState(initial?.name ?? "")
  const [description, setDescription] = useState(initial?.description ?? "")
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit({ slug: slug.trim(), name: name.trim(), description: description.trim() }) }}
      className="space-y-4"
    >
      <label className="block">
        <div className="mb-1 text-xs text-th-fg/60">Slug (e.g. YGC162)</div>
        <input className="input w-full font-mono" value={slug} onChange={(e) => setSlug(e.target.value)} disabled={!!initial} required />
      </label>
      <label className="block">
        <div className="mb-1 text-xs text-th-fg/60">Display name</div>
        <input className="input w-full" value={name} onChange={(e) => setName(e.target.value)} required />
      </label>
      <label className="block">
        <div className="mb-1 text-xs text-th-fg/60">Description</div>
        <textarea className="input w-full min-h-[80px]" value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      {error && <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-2.5 text-sm text-red-400">{error.serverMessage ?? "Save failed."}</div>}
      <button type="submit" className="btn-primary" disabled={submitting}>
        {submitting ? "Saving…" : "Save"}
      </button>
    </form>
  )
}
